import { useState, useEffect } from "react";
import api from "../../../utils/api";

import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    OutlinedInput,
    FormHelperText,
    Button,
    Box,
    FormControl,
    FormLabel,
    Select,
    MenuItem,
    CircularProgress
} from "@mui/material";

const initial = {
    first_name: "",
    last_name: "",
    email: "",
    password: "",
    role: "admin",
};

export default function UserModal({ open, onClose, row = null }) {
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState({});
    const [form, setForm] = useState(initial);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async () => {
        try {
            setSaving(true);

            if (row?.id) {
                await api.put(`admin/users/${row.id}`, form);
            } else {
                await api.post("admin/users", form);
            }

            onClose(true);
        } catch (e) {
            setErrors(e.errors || {});
        } finally {
            setSaving(false);
        }
    };

    useEffect(() => {
        if (row) {
            setForm({
                first_name: row.first_name || "",
                last_name: row.last_name || "",
                email: row.email || "",
                role: row.role || "admin",
                password: "", // Don't pre-fill password
            });
        } else {
            setForm(initial);
        }
        setErrors({});
        setSaving(false);
    }, [row, open]);

    return (
        <Dialog open={open} onClose={() => onClose()} maxWidth="sm" fullWidth>
            <DialogTitle>{!row ? "Create User" : "Edit User"}</DialogTitle>
            <DialogContent>
                <Box display="flex" flexDirection="column" gap={2} mt={1}>
                    <FormControl error={!!errors.first_name?.[0]}>
                        <FormLabel htmlFor="first_name">First Name</FormLabel>
                        <OutlinedInput
                            name="first_name"
                            fullWidth
                            value={form.first_name}
                            onChange={handleChange}
                        />
                        {errors.first_name && (
                            <FormHelperText sx={{ m: 0.5 }} error={!!errors.first_name}>{errors.first_name?.[0]}</FormHelperText>
                        )}
                    </FormControl>

                    <FormControl error={!!errors.last_name?.[0]}>
                        <FormLabel htmlFor="last_name">Last Name</FormLabel>
                        <OutlinedInput
                            name="last_name"
                            fullWidth
                            value={form.last_name}
                            onChange={handleChange}
                        />
                        {errors.last_name && (
                            <FormHelperText sx={{ m: 0.5 }} error={!!errors.last_name}>{errors.last_name?.[0]}</FormHelperText>
                        )}
                    </FormControl>

                    <FormControl error={!!errors.email?.[0]}>
                        <FormLabel htmlFor="email">Email</FormLabel>
                        <OutlinedInput
                            name="email"
                            fullWidth
                            value={form.email}
                            onChange={handleChange}
                        />
                        {errors.email && (
                            <FormHelperText sx={{ m: 0.5 }} error={!!errors.email}>{errors.email?.[0]}</FormHelperText>
                        )}
                    </FormControl>

                    <FormControl error={!!errors.role?.[0]}>
                        <FormLabel htmlFor="role">Role</FormLabel>
                        <Select
                            name="role"
                            value={form.role}
                            onChange={handleChange}
                            error={!!errors.role}
                        >
                            <MenuItem value="admin">Admin</MenuItem>
                            <MenuItem value="super_admin">Super Admin</MenuItem>
                        </Select>
                        {errors.role && (
                            <FormHelperText sx={{ m: 0.5 }} error={!!errors.role}>{errors.role?.[0]}</FormHelperText>
                        )}
                    </FormControl>

                    <FormControl error={!!errors.password?.[0]}>
                        <FormLabel htmlFor="password">{row ? "New Password (leave blank to keep current)" : "Password"}</FormLabel>
                        <OutlinedInput
                            name="password"
                            type="password"
                            fullWidth
                            value={form.password}
                            onChange={handleChange}
                        />
                        {errors.password && (
                            <FormHelperText sx={{ m: 0.5 }} error={!!errors.password}>{errors.password?.[0]}</FormHelperText>
                        )}
                    </FormControl>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => onClose()}>Cancel</Button>
                <Button
                    sx={{color: '#fff'}}
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={saving}
                >
                    {saving ? <CircularProgress size={24} /> : (!row ? "Create" : "Edit")}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
